import { useContext, useMemo } from 'react';

import { ClosedEyeIcon, OpenedEyeIcon, Tooltip } from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';
import { Column } from 'react-table';

import { LocalizationContext } from 'localization';
import { TGroup } from 'types';
import { secondsToWeekDays } from 'utils';

import s from './GoodsGroupTable.module.scss';
import { IUseColumns } from './interfaces';

export const useColumns = ({
  toggleShowInShellCategory,
}: IUseColumns): Column<TGroup>[] => {
  const { t, lang } = useContext(LocalizationContext);

  const dayArr = useMemo(() => {
    const returnedDayName: { title: string; value: number }[] = [];

    for (let day = 1; day < 8; day++) {
      const weekday = dayjs().locale(lang).day(day).format('dd');

      returnedDayName.push({
        title: weekday.charAt(0).toUpperCase() + weekday.slice(1),
        value: day,
      });
    }

    return returnedDayName;
  }, [lang]);

  return useMemo(
    () => [
      {
        Header: t('title'),
        accessor: 'title',
        width: 300,
      },
      {
        Header: t('tariff.schedule_payment'),
        accessor: 'sell_periods',
        Cell: ({ value }: { value: { start: number; end: number }[] }) => {
          if (!value?.length) {
            return <span>—</span>;
          }

          const sorted = [...value].sort((a, b) => a.start - b.start);

          const days = sorted
            .map((p) => secondsToWeekDays(p.start))
            .map(
              (dayValue) => dayArr.find((d) => d.value === dayValue.day)?.title
            )
            .filter(Boolean)
            .join(',');

          const timeStart = secondsToWeekDays(value[0].start).hour;
          const timeEnd = secondsToWeekDays(value[0].end).hour;

          return (
            <span className={s.sellPeriods}>
              <span className={s.days}>{days}</span>
              <span className={s.time}>
                {timeStart}-{timeEnd}
              </span>
            </span>
          );
        },
        width: 300,
      },
      {
        Header: t('group.table.goods.title'),
        accessor: (row: TGroup) => row.goods?.length || 0,
        Cell: ({ value }: { value: number }) =>
          `${value.toLocaleString()} ${t('pcs')}`,
        width: 150,
      },
      {
        Header: t('group.table.show_in_shell.title'),
        accessor: 'show_in_shell',
        width: 250,
        Cell: ({ value, row }) => {
          return (
            <Tooltip
              position={'bottom-right'}
              className={s.tooltip}
              contentClassName={s.tooltip_content}
              content={
                <span style={{ fontSize: '12px', lineHeight: '16px' }}>
                  {t('group.table.show_in_shell.title')}
                </span>
              }
            >
              <div
                onClick={async () =>
                  await toggleShowInShellCategory(row.original)
                }
                className={cn(s.show_icon, value && s.active)}
              >
                {value ? (
                  <OpenedEyeIcon size={'s'} />
                ) : (
                  <ClosedEyeIcon color={'secondary'} size={'s'} />
                )}
              </div>
            </Tooltip>
          );
        },
      },
    ],
    [t]
  );
};
